import { css } from 'aphrodite';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { getFormattedPhoneNumber } from '../../../../../../models/UiUtils';
import {
	invalidateZoomImportablePhoneNumbersQuery,
	useImportZoomPhoneNumberMutation,
	useZoomConnectionQuery,
	useZoomImportablePhoneNumbersQuery,
} from '../../../../../../queries';
import { Button } from '../../../../../components-v2/Button';
import { LoadingSpinner } from '../../../../LoadingSpinner';
import { SuccessCheckIcon } from '../../../../svgs/icons/SuccessCheckIcon';
import { useZoomConnect } from '../../../ZoomConnection/useZoomConnect';
import { styleSheet } from './styles';

export const ZOOM_TEXTING_AUTHORIZE_FEATURE = 'texting';

interface IProps {
	/** Must be a route where this component mounts, or the consent outcome is never shown. */
	connectReturnPath?: string;
	onImported?: () => void | Promise<void>;
}

export function ZoomPhoneTexting({ connectReturnPath = '/settings/personal/texting', onImported }: IProps = {}) {
	const userSession = useUserSession();
	const zoomPhoneEnabled = !!userSession.account.features?.texting?.zoomPhoneEnabled;

	const connectionQuery = useZoomConnectionQuery({
		enabled: zoomPhoneEnabled,
		meta: { toastErrorMessage: 'Failed to load your Zoom connection status' },
	});
	const isConnected = !!connectionQuery.data?.connected;
	const smsEnabled = isConnected && !!connectionQuery.data?.smsEnabled;

	const importableQuery = useZoomImportablePhoneNumbersQuery({ enabled: zoomPhoneEnabled && smsEnabled });

	const { connect, isConnecting } = useZoomConnect({
		connectReturnPath,
		feature: ZOOM_TEXTING_AUTHORIZE_FEATURE,
		onConnected: invalidateZoomImportablePhoneNumbersQuery,
	});

	const importMutation = useImportZoomPhoneNumberMutation({
		onError: error => toast.error(error.systemMessage ?? 'Unable to add your Zoom Phone number'),
		onSuccess: async () => {
			toast.success('Zoom Phone number added for texting');
			await onImported?.();
		},
	});

	if (!zoomPhoneEnabled) {
		return null;
	}

	if (connectionQuery.isPending) {
		return <LoadingSpinner type='small' />;
	}

	if (!isConnected) {
		return (
			<div className={css(styleSheet.container)}>
				<span className={css(styleSheet.description)}>
					Text from your own Zoom Phone number. Messages are sent and archived through Zoom, so your firm&apos;s Zoom
					compliance settings apply.
				</span>
				<div>
					<Button variant='primary' size='sm' isLoading={isConnecting} onClick={connect}>
						Connect Zoom for texting
					</Button>
				</div>
			</div>
		);
	}

	if (!smsEnabled) {
		return (
			<div className={css(styleSheet.container)}>
				<span className={css(styleSheet.description)}>
					Your Zoom account is connected for scheduling. Allow texting access to send texts from your Zoom Phone number.
				</span>
				<div>
					<Button variant='primary' size='sm' isLoading={isConnecting} onClick={connect}>
						Enable texting through Zoom
					</Button>
				</div>
			</div>
		);
	}

	const numbers = importableQuery.data ?? [];

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.statusRow)}>
				<SuccessCheckIcon />
				<span className={css(styleSheet.description)}>
					Zoom connected with texting access{connectionQuery.data?.email ? ` as ${connectionQuery.data.email}` : ''}.
				</span>
			</div>
			{importableQuery.isPending ? (
				<LoadingSpinner type='small' />
			) : importableQuery.isError ? (
				<span className={css(styleSheet.description)}>
					Could not load your Zoom Phone numbers. Reload to try again.
				</span>
			) : numbers.length === 0 ? (
				<span className={css(styleSheet.description)}>
					No Zoom Phone numbers are available to add. Ask your Zoom administrator to assign you an SMS-enabled number.
				</span>
			) : (
				<div className={css(styleSheet.numberList)}>
					<span className={css(styleSheet.description)}>Choose the Zoom Phone number to text from:</span>
					{numbers.map(number => (
						<div key={number.e164} className={css(styleSheet.numberRow)}>
							<span>{getFormattedPhoneNumber(number.standard ?? number.e164) || number.e164}</span>
							<Button
								variant='secondary'
								size='sm'
								isLoading={importMutation.isPending && importMutation.variables?.phoneNumber === number.e164}
								disabled={importMutation.isPending}
								onClick={() => importMutation.mutate({ phoneNumber: number.e164 })}
							>
								Use this number
							</Button>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
